// src/components/common/MultiSelectFilter/MultiSelectFilter.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Check, ChevronDown, Loader2 } from 'lucide-react';
import styles from './MultiSelectFilter.module.css';

const MultiSelectFilter = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  label,
  displayKey = 'label',
  valueKey = 'key',
  searchKey = 'label',
  error,
  renderOptionExtra = null,
  // When provided, the component switches to async mode:
  //   - local filtering is skipped
  //   - onSearch(term) is called as the user types (debounced)
  //   - the parent is responsible for updating `options` with results
  onSearch,
  // Optional: show a loading spinner inside the dropdown while parent fetches
  isLoading = false,
  showSelectAll = false,
  selectAllLabel = 'Select all',
  deselectAllLabel = 'Deselect all',
  floatingDropdown = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // When the dropdown closes, clear the search term so it resets next time
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Debounced async search — only fires when onSearch prop is provided
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);

    if (!onSearch) return;

    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      onSearch(term);
    }, 300);
  }, [onSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // In async mode, the parent controls which options are shown —
  // skip local filtering. In sync mode, filter locally as before.
  const filteredOptions = onSearch
    ? options
    : options.filter(option =>
        option[searchKey]?.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const handleToggle = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getSelectedLabels = () => {
    return selectedValues
      .map(value => options.find(opt => opt[valueKey] === value)?.[displayKey])
      .filter(Boolean);
  };

  const selectedLabels = getSelectedLabels();

  // Determine empty state message
  const getEmptyMessage = () => {
    if (isLoading) return null; // spinner shown separately
    if (onSearch && !searchTerm) return 'Type to search...';
    if (onSearch && searchTerm && searchTerm.length < 2) return 'Type at least 2 characters...';
    return 'No options found';
  };

  return (
    <div className={styles.multiSelectWrapper} ref={dropdownRef}>
      {label && (
        <label className={styles.label}>
          {label}
        </label>
      )}

      <div
        className={`${styles.selectTrigger} ${error ? styles.error : ''} ${isOpen ? styles.open : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.selectedValues}>
          {selectedLabels.length === 0 ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : (
            <div className={styles.selectedTags}>
              {selectedLabels.slice(0, 2).map((lbl, index) => (
                <span key={index} className={styles.selectedTag}>
                  {lbl}
                </span>
              ))}
              {selectedLabels.length > 2 && (
                <span className={styles.moreCount}>
                  +{selectedLabels.length - 2} more
                </span>
              )}
            </div>
          )}
        </div>
        <ChevronDown
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`}
          size={18}
        />
      </div>

      {isOpen && (
        <div className={`${styles.dropdown} ${floatingDropdown ? styles.dropdownFloating : ''}`}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={onSearch ? 'Type to search...' : searchPlaceholder}
              className={styles.searchInput}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {isLoading && (
              <Loader2
                size={14}
                className={styles.searchSpinner}
              />
            )}
            {searchTerm && !isLoading && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSearchChange('');
                }}
                className={styles.clearSearch}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {showSelectAll ? (
            <div className={styles.selectedHeader}>
              <span>{selectedValues.length} selected</span>
              {options.length > 0 && options.every(opt => selectedValues.includes(opt[valueKey])) ? (
                <button onClick={handleClearAll} className={styles.clearAll}>
                  {deselectAllLabel}
                </button>
              ) : (
                <button
                  onClick={() => onChange(options.map(opt => opt[valueKey]))}
                  className={styles.selectAll}
                >
                  {selectAllLabel}
                </button>
              )}
            </div>
          ) : selectedValues.length > 0 && (
            <div className={styles.selectedHeader}>
              <span>{selectedValues.length} selected</span>
              <button onClick={handleClearAll} className={styles.clearAll}>
                Clear all
              </button>
            </div>
          )}

          <div className={styles.optionsList}>
            {isLoading ? (
              <div className={styles.loadingState}>
                <Loader2 size={18} className={styles.loadingSpinner} />
                <span>Searching...</span>
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option[valueKey]);
                return (
                  <div
                    key={option[valueKey]}
                    className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(option[valueKey]);
                    }}
                  >
                    <div className={styles.checkbox}>
                      {isSelected && <Check size={14} />}
                    </div>
                    <span className={styles.optionLabel}>
                      {option[displayKey]}
                    </span>
                    {renderOptionExtra && (
                      <div
                        className={styles.optionExtra}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {renderOptionExtra(option, isSelected)}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className={styles.noOptions}>
                {getEmptyMessage()}
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <span className={styles.errorText}>{error}</span>
      )}
    </div>
  );
};

export default MultiSelectFilter;